/* eslint-disable import/no-extraneous-dependencies */
/* globals mapboxgl */
import { setupGalleryRouting } from './gallery';
import renderDetailView from './creative-challenge-entry-detail'

const defaultMapboxStyle = 'mapbox://styles/elondaits/ckzy875b2000215mpk2kvm271/draft';

export default class CreativeChallengeMap {
  constructor(dataManager, mapBoxAPIKey, options) {
    this.dataManager = dataManager;
    this.$element = $('<div></div>')
      .addClass('creative-challenge-map');
    this.mapBoxAPIKey = mapBoxAPIKey;
    const defaultOptions = {
      mapboxStyleUrl: defaultMapboxStyle,
      center: [16.212880397276166, 31.406099880934292],
      zoom: 1,
      maxZoom: 14,
      minZoom: 1,
      pitchWithRotate: false,
    };
    this.options = { ...defaultOptions, options };
    this.map = null;
  }

  async init() {
    mapboxgl.accessToken = this.mapBoxAPIKey;
    const items = await this.dataManager.getEntriesData();
    this.createMap(items);
    this.installRouteListener(items)
  }

  // eslint-disable-next-line class-methods-use-this
  installRouteListener(items) {
    setupGalleryRouting(
      Object.fromEntries(items.map((item) => [item.route, item])),
      renderDetailView,
      {
        modalSize: 'xl',
        modalClass: 'photo-challenge-modal',
      }
    );
  }

  createMap(items) {
    const map = new mapboxgl.Map({
      container: this.$element[0],
      style: this.options.mapboxStyleUrl,
      center: this.options.center,
      zoom: this.options.zoom,
      maxZoom: this.options.maxZoom,
      minZoom: this.options.minZoom,
      pitchWithRotate: this.options.pitchWithRotate,
    });

    map.addControl(new mapboxgl.NavigationControl({
      showCompass: false,
      showZoom: true,
      visualizePitch: false,
    }), 'bottom-right');

    map.addControl(new mapboxgl.GeolocateControl({
      showUserLocation: false,
      showAccuracyCircle: false,
    }), 'bottom-right');

    const d = 0.004;
    const r = 0.0005;
    const k = (2 * d) / r;
    const clusters = {};
    function spiralCoordinates(lon, lat) {
      const key = `${lat},${lon}`;
      if (clusters[key] !== undefined) {
        clusters[key] += 1;
      } else {
        clusters[key] = 0;
      }
      const i = clusters[key];
      return [
        lon + r * Math.sqrt(k * (i + 0.5)) * Math.cos(Math.sqrt(k * (i + 0.5))),
        lat + r * Math.sqrt(k * (i + 0.5)) * Math.sin(Math.sqrt(k * (i + 0.5)))
        * Math.cos((lat / 180) * Math.PI),
      ];
    }

    const geojson = {
      type: 'FeatureCollection',
      features: items.map((item) => ({
        type: 'Feature',
        properties: {
          id: item.id,
          image: item.miniThumbnail,
        },
        geometry: {
          type: 'Point',
          coordinates: spiralCoordinates(Number(item.lon), Number(item.lat)),
        },
      })),
    };

    let shownMarkers = {};

    const onMarkerClick = (ev) => {
      const id = $(ev.target).data('id');
      window.location.replace(`#${this.dataManager.getPhotoRoutePrefix()}${id}`);
    };

    const createMarker = (id, imageUrl) => {
      const element = $('<div></div>')
        .addClass('photo-marker')
        .append(
          $('<div></div>')
            .addClass('photo')
            .css({ backgroundImage: `url(${imageUrl})` })
            .data('id', id)
        )
        .on('click', onMarkerClick);
      return new mapboxgl.Marker(element[0]);
    };

    const destroyMarker = (marker) => {
      $(marker.getElement()).off('click', onMarkerClick);
    };

    const updateMarkers = () => {
      const newMarkers = {};
      const features = map.querySourceFeatures('photos');
      // Query the map for features that intersect the current viewport.
      features.forEach((feature) => {
        // Create markers for features that are not clusters
        if (!feature.properties.cluster) {
          const { id, image } = feature.properties;
          // See if there is an existing marker for this feature.
          // The querySourceFeatures method returns duplicates, for some reason
          if (newMarkers[id] === undefined) {
            // If the marker already exists, use it. If not, create a new one
            let marker = shownMarkers[id];
            if (!marker) {
              marker = createMarker(id, image)
                .setLngLat(feature.geometry.coordinates)
                .addTo(map);
            }
            newMarkers[id] = marker;
          }
        }
      });
      Object.keys(shownMarkers).forEach((id) => {
        if (!newMarkers[id]) {
          destroyMarker(shownMarkers[id]);
          shownMarkers[id].remove();
        }
      });
      shownMarkers = newMarkers;
    };

    map.on('load', () => {
      map.addSource('photos', {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: 13, // Max zoom to cluster points on
        clusterRadius: 65, // Radius of each cluster when clustering points (defaults to 50)
      });

      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'photos',
        filter: ['has', 'point_count'],
        paint: {
          // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
          // with three steps to implement three types of circles:
          //   * Blue, 20px circles when point count is less than 100
          //   * Yellow, 30px circles when point count is between 100 and 750
          //   * Pink, 40px circles when point count is greater than or equal to 750
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#f58220',
            100,
            '#ffcb05',
            1000,
            '#a6ce39',
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            100,
            30,
            750,
            40,
          ],
        },
        maxzoom: 14,
      });

      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'photos',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 16,
        },
        maxzoom: 14,
      });

      map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'photos',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#c657a0',
          'circle-radius': 12,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
        },
      });

      setTimeout(() => {
        updateMarkers();
      }, 1000);
    });

    map.on('click', 'clusters', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });
      const clusterId = features[0].properties.cluster_id;
      map.getSource('photos').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return;

          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom,
          });
        }
      );
    });

    map.on('render', () => {
      updateMarkers();
    });

    this.map = map;
    window.map = map;
  }
}
